import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Zoom,
  useTheme,
  alpha,
  styled,
  Tooltip,
  CircularProgress,
  Chip,
  Alert,
  Snackbar,
  Typography
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
// import MicOffIcon from '@mui/icons-material/MicOff';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { motion, AnimatePresence } from 'framer-motion';
import { transcribeAudio, validateAudioFile } from 'services/transcriptionService';

// Styled components with modern design
const StyledChatContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: '100%',
  margin: 0,
  padding: theme.spacing(2),
}));

const StyledInputPaper = styled(Paper)(({ theme }) => ({
  borderRadius: '28px',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
  background: `linear-gradient(145deg, ${theme.palette.background.paper}, ${alpha(theme.palette.primary.main, 0.02)})`,
  backdropFilter: 'blur(20px)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.12)}`,
    transform: 'translateY(-2px)',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  },
  '&:focus-within': {
    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}, 0 12px 40px ${alpha(theme.palette.common.black, 0.15)}`,
    transform: 'translateY(-4px)',
    border: `1px solid ${theme.palette.primary.main}`,
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    border: 'none',
    borderRadius: '28px',
    paddingRight: '20px',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: 1.5,
    minHeight: '48px',
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: 'none',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 16px',
    '&::placeholder': {
      color: alpha(theme.palette.text.secondary, 0.7),
      opacity: 1,
    },
  },
  '& .MuiInputBase-inputMultiline': {
    padding: '12px 16px',
    maxHeight: '100px',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: alpha(theme.palette.primary.main, 0.3),
      borderRadius: '3px',
    },
  },
}));

const ActionButton = styled(IconButton)(({ theme, variant }) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'send':
        return {
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: theme.palette.primary.contrastText,
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
            boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.4)}`,
          },
        };
      case 'accept':
        return {
          background: theme.palette.grey[200],
          color: theme.palette.success.main,
          '&:hover': {
            background: theme.palette.grey[300],
          },
        };
      case 'cancel':
        return {
          background: theme.palette.grey[200],
          color: theme.palette.grey[600],
          '&:hover': {
            background: theme.palette.grey[300],
          },
        };
      case 'stop':
        return {
          background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
          color: theme.palette.warning.contrastText,
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.warning.dark}, ${theme.palette.warning.main})`,
            boxShadow: `0 8px 25px ${alpha(theme.palette.warning.main, 0.4)}`,
          },
        };
      default:
        return {
          background: theme.palette.grey[200],
          color: theme.palette.grey[700],
          '&:hover': {
            background: theme.palette.grey[300],
            boxShadow: `0 6px 20px ${alpha(theme.palette.common.black, 0.1)}`,
          },
        };
    }
  };

  return {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    margin: '0 4px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    ...getVariantStyles(),
    '&:hover': {
      transform: 'scale(1.1)',
      ...getVariantStyles()['&:hover'],
    },
    '&:active': {
      transform: 'scale(0.95)',
    },
    '&.recording': {
      background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
      color: theme.palette.error.contrastText,
      animation: 'pulse 2s infinite',
    },
    '@keyframes pulse': {
      '0%': {
        boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0.7)}`,
      },
      '70%': {
        boxShadow: `0 0 0 10px ${alpha(theme.palette.error.main, 0)}`,
      },
      '100%': {
        boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0)}`,
      },
    },
  };
});

const SuggestionChip = styled(Chip)(({ theme }) => ({
  margin: '4px 6px 4px 0',
  borderRadius: '16px',
  background: alpha(theme.palette.primary.main, 0.1),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  color: theme.palette.primary.main,
  fontWeight: 500,
  transition: 'all 0.2s ease',
  '&:hover': {
    background: alpha(theme.palette.primary.main, 0.2),
    transform: 'translateY(-1px)',
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
  },
}));

const TypingIndicator = ({ show }) => {
  const theme = useTheme();
  
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.2 }}
        >
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              p: 2,
              color: theme.palette.text.secondary,
              fontSize: '14px',
            }}
          >
            <SmartToyIcon sx={{ fontSize: '18px' }} />
            <span>AI is thinking</span>
            <Box sx={{ display: 'flex', gap: '2px', ml: 1 }}>
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  animate={{ 
                    scale: [1, 1.2, 1],
                    opacity: [0.7, 1, 0.7] 
                  }}
                  transition={{ 
                    duration: 1.5, 
                    repeat: Infinity, 
                    delay: i * 0.2 
                  }}
                  style={{
                    width: '4px',
                    height: '4px',
                    borderRadius: '50%',
                    backgroundColor: theme.palette.primary.main,
                  }}
                />
              ))}
            </Box>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const AudioVisualization = ({ isRecording, audioLevels }) => {
  const theme = useTheme();
  
  // Use real audio levels or fallback to low levels when not recording
  const levels = audioLevels.length > 0 ? audioLevels : Array(20).fill(0.1);
  
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '2px',
        height: '40px',
        px: 2,
      }}
    >
      {levels.map((level, index) => (
        <motion.div
          key={index}
          animate={{
            scaleY: isRecording ? Math.max(0.2, Math.min(1, level * 1.5)) : 0.2,
            opacity: isRecording ? 0.6 + (level * 0.4) : 0.3
          }}
          transition={{
            duration: 0.1,
            ease: "easeOut"
          }}
          style={{
            width: '3px',
            height: '100%',
            backgroundColor: isRecording 
              ? (level > 0.5 ? theme.palette.error.main : theme.palette.primary.main)
              : theme.palette.action.disabled,
            borderRadius: '1.5px',
            transformOrigin: 'bottom',
          }}
        />
      ))}
    </Box>
  );
};

const PremiumChatInput = ({ 
  onSendMessage, 
  placeholder = "Ask me anything...", 
  suggestions = ["How can I improve my product?", "Analyze competitor features", "Generate user personas"],
  disabled = false,
  showTyping = false,
  autoFocus = false,
  actionsPlacement = 'below' // 'below' | 'right'
}) => {
  const theme = useTheme();
  const [message, setMessage] = useState('');
  const [voiceState, setVoiceState] = useState('idle'); // 'idle', 'recording', 'transcribing'
  const [isSending, setIsSending] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);
  const [audioLevels, setAudioLevels] = useState([]);
  
  const textFieldRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    if (autoFocus && textFieldRef.current) {
      textFieldRef.current.focus();
    }
  }, [autoFocus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && voiceState === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, [voiceState]);

  const handleSend = async () => {
    if (message.trim() && !disabled && !isSending) {
      setIsSending(true);
      setShowSuggestions(false);
      
      try {
        await onSendMessage?.(message.trim());
        setMessage('');
      } catch (error) {
        console.error('Error sending message:', error);
      } finally {
        setIsSending(false);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        } 
      });
      
      // Set up audio context for visualization
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      
      // Configure analyser for real-time volume detection
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.3;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      const timeDataArray = new Uint8Array(analyser.fftSize);
      
      source.connect(analyser);
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      
      // Start audio level monitoring for volume-based visualization
      const updateAudioLevels = () => {
        if (mediaRecorderRef.current?.state === 'recording' && analyserRef.current) {
          // Get time-domain data for volume calculation
          analyserRef.current.getByteTimeDomainData(timeDataArray);
          
          // Calculate RMS (Root Mean Square) for overall volume
          let sum = 0;
          for (let i = 0; i < timeDataArray.length; i++) {
            const value = (timeDataArray[i] - 128) / 128; // Convert to -1 to 1 range
            sum += value * value;
          }
          const rms = Math.sqrt(sum / timeDataArray.length);
          const volume = Math.min(1, rms * 3); // Amplify for better visualization
          
          // Get frequency data for bar variation
          analyserRef.current.getByteFrequencyData(dataArray);
          
          // Create 20 bars with volume-based heights
          const barCount = 20;
          const levels = [];
          
          for (let i = 0; i < barCount; i++) {
            // Use frequency data for variation between bars
            const freqIndex = Math.floor((i / barCount) * bufferLength);
            const frequencyValue = dataArray[freqIndex] / 255;
            
            // Combine overall volume with frequency-specific data
            const baseLevel = volume * 0.7; // 70% from overall volume
            const freqVariation = frequencyValue * 0.3; // 30% from frequency data
            const combinedLevel = baseLevel + freqVariation;
            
            // Add slight random variation for more natural look
            const randomFactor = 0.9 + (Math.random() * 0.2);
            levels.push(Math.min(1, combinedLevel * randomFactor));
          }
          
          setAudioLevels(levels);
        //   console.log('Audio Levels:', levels);
          animationFrameRef.current = requestAnimationFrame(updateAudioLevels);
        }
      };
      
      // Clear previous audio chunks
      audioChunksRef.current = [];
      
      // Create MediaRecorder with optimal settings for Whisper
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
          ? 'audio/webm;codecs=opus' 
          : 'audio/webm'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
        
        // Clean up audio analysis
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
          audioContextRef.current.close();
        }
        
        setAudioLevels([]);
      };
      
      mediaRecorder.start();
      setVoiceState('recording');
      updateAudioLevels();
      setError('');
    } catch (error) {
      console.error('Error starting recording:', error);
      setError('Unable to access microphone. Please check permissions.');
      setShowError(true);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop(); // This triggers onstop for cleanup
    }
    
    audioChunksRef.current = [];
    setVoiceState('idle');
    setAudioLevels([]);
  };

  const acceptRecording = async () => {
    // Stop recording first
    if (mediaRecorderRef.current && voiceState === 'recording') {
      mediaRecorderRef.current.stop();
    }
    
    // Start transcription
    setVoiceState('transcribing');
    
    // Wait a bit for the recording to finish processing
    setTimeout(async () => {
      await handleTranscription();
    }, 100);
  };

  const handleTranscription = async () => {
    if (audioChunksRef.current.length === 0) {
      setError('No audio recorded');
      setShowError(true);
      setVoiceState('idle');
      return;
    }
    
    try {
      // Create audio blob from recorded chunks
      const audioBlob = new Blob(audioChunksRef.current, { 
        type: 'audio/webm' 
      });
      
      // Create a File object for the transcription service
      const audioFile = new File([audioBlob], 'recording.webm', {
        type: 'audio/webm'
      });
      
      // Validate the audio file
      const validation = validateAudioFile(audioFile);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }
      
      // Transcribe the audio
      const result = await transcribeAudio({ 
        audioFile,
        language: null, // Auto-detect language
        prompt: "Transcribe this voice message for a business chat interface." 
      });
      
      if (result.success && result.transcription) {
        // Set the transcribed text as the message
        setMessage(result.transcription);
        setShowSuggestions(false);
        textFieldRef.current?.focus();
      } else {
        throw new Error('Transcription failed');
      }
    } catch (error) {
      console.error('Error transcribing audio:', error);
      setError(error.message || 'Failed to transcribe audio');
      setShowError(true);
    } finally {
      setVoiceState('idle');
      audioChunksRef.current = [];
    }
  };

  const handleVoiceToggle = () => {
    if (voiceState === 'idle') {
      startRecording();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setMessage(suggestion);
    setShowSuggestions(false);
    textFieldRef.current?.focus();
  };

  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Handle file upload here
      console.log('File selected:', file.name);
    }
  };

  return (
    <StyledChatContainer>
      {/* Suggestions */}
      <Box sx={{ minHeight: showSuggestions && suggestions.length > 0 && !message ? 'auto' : 0, overflow: 'hidden' }}>
        <AnimatePresence mode="wait">
          {showSuggestions && suggestions.length > 0 && !message && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
            >
              <Box sx={{ mb: 2, textAlign: 'center', py: 1 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
                  {suggestions.map((suggestion, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <SuggestionChip
                        label={suggestion}
                        onClick={() => handleSuggestionClick(suggestion)}
                        clickable
                      />
                    </motion.div>
                  ))}
                </Box>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>

      {/* Typing Indicator */}
      <TypingIndicator show={showTyping} />

      {/* Main Input */}
      <StyledInputPaper elevation={0}>
        <Box sx={{ position: actionsPlacement === 'right' ? 'relative' : 'static' }}>
          <StyledTextField
            ref={textFieldRef}
            fullWidth
            multiline
            maxRows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            disabled={disabled || isSending}
            variant="outlined"
          />

          {/* Action Buttons */}
          {actionsPlacement === 'right' ? (
            <Box
              sx={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <AnimatePresence mode="wait">
                {voiceState === 'idle' && (
                  <motion.div
                    key="voice-idle"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <Tooltip title="Attach File" arrow>
                      <ActionButton
                        onClick={handleFileUpload}
                        disabled={disabled || isSending}
                        variant="attach"
                      >
                        <AttachFileIcon fontSize="small" />
                      </ActionButton>
                    </Tooltip>

                    <Tooltip title="Voice Input" arrow>
                      <ActionButton
                        onClick={handleVoiceToggle}
                        disabled={disabled || isSending}
                        variant="voice"
                      >
                        <MicIcon fontSize="small" />
                      </ActionButton>
                    </Tooltip>

                    <Tooltip title="Send Message" arrow>
                      <Zoom in={message.trim().length > 0 || isSending}>
                        <ActionButton
                          onClick={handleSend}
                          disabled={!message.trim() || disabled || isSending}
                          variant="send"
                        >
                          {isSending ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <SendIcon fontSize="small" />
                          )}
                        </ActionButton>
                      </Zoom>
                    </Tooltip>
                  </motion.div>
                )}

                {voiceState === 'recording' && (
                  <motion.div
                    key="voice-recording"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      background: alpha(theme.palette.primary.main, 0.1),
                      borderRadius: '22px',
                      padding: '4px',
                      marginRight: '4px'
                    }}
                  >
                    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                      <AudioVisualization 
                        isRecording={true} 
                        audioLevels={audioLevels} 
                      />
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', pl: 1 }}>
                      <Tooltip title="Cancel" arrow>
                        <ActionButton
                          onClick={cancelRecording}
                          disabled={disabled}
                          variant="cancel"
                          sx={{ mx: 0.5 }}
                        >
                          <CloseIcon fontSize="small" />
                        </ActionButton>
                      </Tooltip>

                      <Tooltip title="Accept" arrow>
                        <ActionButton
                          onClick={acceptRecording}
                          disabled={disabled}
                          variant="accept"
                          sx={{ mx: 0.5 }}
                        >
                          <CheckIcon fontSize="small" />
                        </ActionButton>
                      </Tooltip>
                    </Box>
                  </motion.div>
                )}

                {voiceState === 'transcribing' && (
                  <motion.div
                    key="voice-transcribing"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      background: alpha(theme.palette.primary.main, 0.1),
                      borderRadius: '22px',
                      padding: '8px 16px',
                      marginRight: '4px'
                    }}
                  >
                    <CircularProgress size={16} color="primary" sx={{ mr: 1 }} />
                    <Typography variant="body2" color="primary">
                      Transcribing...
                    </Typography>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
              <AnimatePresence mode="wait">
                {voiceState === 'idle' && (
                  <motion.div
                    key="voice-idle"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <Tooltip title="Attach File" arrow>
                      <ActionButton
                        onClick={handleFileUpload}
                        disabled={disabled || isSending}
                        variant="attach"
                      >
                        <AttachFileIcon sx={{ fontSize: 18 }} />
                      </ActionButton>
                    </Tooltip>

                    <Tooltip title="Voice Input" arrow>
                      <ActionButton
                        onClick={handleVoiceToggle}
                        disabled={disabled || isSending}
                        variant="voice"
                      >
                        <MicIcon sx={{ fontSize: 18 }} />
                      </ActionButton>
                    </Tooltip>

                    <Tooltip title="Send Message" arrow>
                      <Zoom in={message.trim().length > 0 || isSending}>
                        <ActionButton
                          onClick={handleSend}
                          disabled={!message.trim() || disabled || isSending}
                          variant="send"
                        >
                          {isSending ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <SendIcon sx={{ fontSize: 18 }} />
                          )}
                        </ActionButton>
                      </Zoom>
                    </Tooltip>
                  </motion.div>
                )}

                {voiceState === 'recording' && (
                  <motion.div
                    key="voice-recording"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      background: alpha(theme.palette.primary.main, 0.1),
                      borderRadius: '18px',
                      padding: '2px 4px',
                      marginRight: '4px'
                    }}
                  >
                    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                      <AudioVisualization 
                        isRecording={true} 
                        audioLevels={audioLevels} 
                      />
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', pl: 0.5 }}>
                      <Tooltip title="Cancel" arrow>
                        <ActionButton
                          onClick={cancelRecording}
                          disabled={disabled}
                          variant="cancel"
                          sx={{ mx: 0.25 }}
                        >
                          <CloseIcon sx={{ fontSize: 18 }} />
                        </ActionButton>
                      </Tooltip>

                      <Tooltip title="Accept" arrow>
                        <ActionButton
                          onClick={acceptRecording}
                          disabled={disabled}
                          variant="accept"
                          sx={{ mx: 0.25 }}
                        >
                          <CheckIcon sx={{ fontSize: 18 }} />
                        </ActionButton>
                      </Tooltip>
                    </Box>
                  </motion.div>
                )}

                {voiceState === 'transcribing' && (
                  <motion.div
                    key="voice-transcribing"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      background: alpha(theme.palette.primary.main, 0.1),
                      borderRadius: '18px',
                      padding: '6px 12px',
                      marginRight: '4px'
                    }}
                  >
                    <CircularProgress size={14} color="primary" sx={{ mr: 1 }} />
                    <Typography variant="body2" color="primary">
                      Transcribing...
                    </Typography>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
          )}
        </Box>
      </StyledInputPaper>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
        accept="*/*"
      />

      {/* Error Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowError(false)} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </StyledChatContainer>
  );
};

export default PremiumChatInput; 